import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { ColorHandle } from '@gik/ui/ColorPicker/components/ColorHandle';
import React from 'react';
import type { Color } from 'react-color';
import { ColorPicker } from './ColorPicker';

const defaultGradient = [
  { color: '#7AFFE7', position: 0 },
  { color: '#003F88', position: 100 },
];

export type GradientValue = {
  color: Color;
  position: number;
};

export type GradientPickerProps = {
  onChange?: (colors: GradientValue[]) => void;
  onSubmit?: (colors: GradientValue[]) => void;
  value?: GradientValue[];
  initialValue?: GradientValue[];
} & UIComponent;

export function parseColorsToGradientValues(colors: GradientValue[]) {
  if (!colors) return undefined;
  return colors
    .map((value, i) => {
      return `${value.color} ${value.position}%`;
    })
    .join(',');
}

export function GradientPicker({
  onSubmit,
  onChange,
  initialValue,
  value,
  className,
  ...otherProps
}: GradientPickerProps) {
  const bem = useBemCN('gradient-picker');
  const controlled = !!value;
  const [_value, _setValue] = React.useState<GradientValue[]>(initialValue || defaultGradient);
  const [selectedStep, setSelectedStep] = React.useState<number>(0);

  const realValue = React.useMemo(() => {
    return (controlled ? value : _value) || [];
  }, [controlled, value, _value]);

  const handleColorChange = React.useCallback(
    (color: Color) => {
      const newValue = realValue.concat([]);
      newValue[selectedStep].color = color;
      onChange?.(newValue);

      if (!controlled) _setValue(newValue);
    },
    [selectedStep, controlled, realValue, onChange]
  );

  const handlePickColor = React.useCallback((index: number) => {
    setSelectedStep(index);
  }, []);

  const handleSubmit = React.useCallback(() => {
    onSubmit?.(realValue);
  }, [realValue, onSubmit]);

  const gradient = realValue ? parseColorsToGradientValues(realValue) : '';

  return (
    <div {...bem(null, null, className)} {...otherProps}>
      <div {...bem('editor')}>
        <div
          {...bem('preview')}
          style={{
            background: `linear-gradient(90deg,${gradient} )`,
          }}
        />
        <div {...bem('controls')}>
          {Array.from(new Array(realValue?.length))?.map((_v, i) => {
            const stepValue = realValue[i];
            if (!stepValue) return null;
            return (
              <ColorHandle
                key={i}
                onClick={() => handlePickColor(i)}
                color={stepValue?.color}
                active={selectedStep === i}
                // @ts-ignore
                style={{ left: stepValue?.position + '%' }}
              />
            );
          })}
        </div>
      </div>

      <ColorPicker value={realValue[selectedStep]?.color} onChange={handleColorChange} onSubmit={handleSubmit} />
    </div>
  );
}
